import styled from '@emotion/styled';
import type { ObjectId } from '@racemap/sdk/schema/base';
import type { Tracker } from '@racemap/sdk/schema/trackers';
import { USER_ROLE } from '@racemap/utilities/consts/trackers';
import { getUserRoleForTracker } from '@racemap/utilities/functions/trackers';
import type { TrackerUser } from '@racemap/utilities/types/trackers';
import type { User_Legacy } from '@racemap/utilities/types/users';
import { Popconfirm, Space } from 'antd';
import type { Immutable } from 'immer';
import moment from 'moment';
import type { FC } from 'react';
import { type Column, SimpleTable } from '../../BasicComponents/SimpleTable';
import { UserInfos } from '../../BasicComponents/UserChooser/UserInfos';
import { IconTrash, getUserRoleIcon } from '../../Icon';

interface Props {
  tracker: Immutable<Tracker>;
  currentUser: Immutable<User_Legacy>;
  onUserRemove?: (
    userId: ObjectId,
    userRole: USER_ROLE,
    dates?: { startTime: Date; endTime: Date },
  ) => void;
}

export const TrackerUserRoleTable: FC<Props> = ({ tracker, currentUser, onUserRemove }) => {
  const { editors, borrowers, owner } = tracker;
  const trackerUsers: Array<TrackerUser> = [
    { role: USER_ROLE.OWNER, id: owner },
    ...editors.map((editor) => ({ role: USER_ROLE.EDITOR, id: editor })),
    ...borrowers
      .map((borrower) => ({
        role: USER_ROLE.BORROWER,
        ...borrower,
      }))
      .sort((a, b) => b.startTime.valueOf() - a.startTime.valueOf()),
  ];

  const columns = [
    {
      header: '#',
      headerClassName: 'center',
      dataKey: 'id',
      width: '35px',
      cellRenderer: (_c, _r, i) => i + 1,
      key: 'index',
      cellClassName: 'center',
    } as Column<TrackerUser, 'id'>,
    {
      header: 'Role',
      dataKey: 'role',
      cellRenderer: (c) => <RoleLabel userRole={c} />,
      key: 'role',
    } as Column<TrackerUser, 'role'>,
    {
      dataKey: 'id',
      header: 'User',
      cellRenderer: (_c, r) => <UserInfos userId={r.id} withAvatar={false} />,
      key: 'user',
    } as Column<TrackerUser, 'id'>,
    {
      header: '',
      dataKey: 'id',
      cellRenderer: (_c, r) => (
        <RemoveCell
          targetUser={trackerUsers.find(
            (tU) => tU.id.toHexString() === r.id.toHexString() && r.role === tU.role,
          )}
          currentUser={currentUser}
          tracker={tracker}
          onRemove={onUserRemove}
          targetUserRole={r.role}
        />
      ),
      key: 'maintain',
    } as Column<TrackerUser, 'id'>,
  ] as Array<Column<TrackerUser>>;

  return (
    <SimpleTable
      columns={columns}
      data={trackerUsers}
      getRowId={getRowId}
      getRowIsExpandable={(r) => r.role === USER_ROLE.BORROWER}
      subRowRenderer={(r) => {
        if (r.startTime == null || r.endTime == null)
          return <>Error: Only Borrowers have sub row!</>;
        return <SubRow lendingStart={r.startTime} lendingEnd={r.endTime} />;
      }}
    />
  );
};

const getRowId = (r: TrackerUser, i: number) => {
  if (r.role !== USER_ROLE.BORROWER) return r.id.toHexString();
  return `${r.id.toHexString()}_${r.startTime?.valueOf()}_${r.endTime?.valueOf()}_${i}`;
};

const UserRoleLabels: Record<USER_ROLE, string> = {
  [USER_ROLE.OWNER]: 'Owner',
  [USER_ROLE.EDITOR]: 'Editor',
  [USER_ROLE.BORROWER]: 'Borrower',
};

const RoleLabel: FC<{ userRole?: string | null }> = ({ userRole }) => {
  if (userRole == null) return <></>;
  if (!Object.values<string>(USER_ROLE).includes(userRole)) return <></>;

  return (
    <Label>
      {getUserRoleIcon(userRole as USER_ROLE)} <Role>{UserRoleLabels[userRole as USER_ROLE]}</Role>
    </Label>
  );
};

const RemoveCell: FC<{
  targetUser: TrackerUser | undefined;
  tracker: Immutable<Tracker>;
  currentUser: Immutable<User_Legacy>;
  onRemove?: (
    userId: ObjectId,
    userRole: USER_ROLE,
    dates?: { startTime: Date; endTime: Date },
  ) => void;
  targetUserRole: USER_ROLE;
}> = ({ targetUser, tracker, currentUser, onRemove, targetUserRole }) => {
  if (targetUser == null) return <></>;
  const currentUserRole = getUserRoleForTracker(tracker, currentUser.id);
  let dates: { startTime: Date; endTime: Date } | undefined;
  if (targetUserRole == null) return <></>;

  if (!currentUser.admin) {
    if (currentUserRole == null || targetUserRole == null) return <></>;
    if (targetUser.id.toHexString() === currentUser.id) return <></>;
    if (currentUserRole === USER_ROLE.BORROWER) return <></>;
    if (currentUserRole === USER_ROLE.EDITOR && targetUserRole !== USER_ROLE.BORROWER) return <></>;
    if (currentUserRole === USER_ROLE.OWNER && targetUserRole === USER_ROLE.OWNER) return <></>;
  }

  // If you are admin and owner, it is useless to remove the user
  if (
    currentUser.admin &&
    currentUserRole === USER_ROLE.OWNER &&
    targetUserRole === USER_ROLE.OWNER
  )
    return <></>;

  if (
    targetUserRole === USER_ROLE.BORROWER &&
    targetUser.startTime != null &&
    targetUser.endTime != null
  ) {
    dates = { startTime: targetUser.startTime, endTime: targetUser.endTime };
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Popconfirm onConfirm={() => onRemove?.(targetUser.id, targetUserRole, dates)} title="Sure?">
        <IconTrash style={{ cursor: 'pointer' }} />
      </Popconfirm>
    </div>
  );
};

const SubRow: FC<{ lendingStart: Date; lendingEnd: Date }> = ({ lendingStart, lendingEnd }) => (
  <SubRowContainer size="large">
    <div>
      Lending starts at: <DateField>{moment(lendingStart).format('ll')}</DateField>
    </div>
    <div>
      Ends at: <DateField>{moment(lendingEnd).format('ll')}</DateField>
    </div>
  </SubRowContainer>
);

const SubRowContainer = styled(Space)`
  padding: 10px;
  width: 100%;

  div {
    display: flex;
    gap: 10px;
  }
`;
const DateField = styled.div`
  font-weight: 600;
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;
const Role = styled.div`
  margin-left: 5px;
  font-weight: 600;
`;
